import { usePageTitle } from "hooks/usePageTitle";
import { useFilters } from "hooks/useFilter";
import { Filter } from "components/DesignSystem/Filter/Filter";
import { Chip } from "components/DesignSystem/Chips/Chips";
import { BooksDateFilter } from "components/Filters/BooksDateFilter";
import Card from "components/DesignSystem/Card/Card";
import { Button } from "components/DesignSystem/Button/Button";
import { DownloadIcon } from "components/icons/Download";
import {
  ACCOUNTING_METHODS,
  ACCOUNTING_METHODS_OPTIONS,
  ACCOUNTING_METHOD_LABELS,
  datePeriod,
} from "constants/bookkeeping";
import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { formatDate } from "utils/formatDate";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { AccountingMethodBanner } from "components/BooksSettings/AccountingMethodBanner";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import Radio from "components/DesignSystem/RadioGroup/RadioGroup";
import { getDateRangeWithSetting } from "utils/getDateRange";
import { useGetStartingDateQuery } from "store/apis/chartOfAccounts";
import * as DATE_PERIOD from "constants/dateFilter";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { useModal } from "hooks/useModal";
import { ExportConsolidatedReportModal } from "./ExportConsolidatedReportModal/ExportConsolidatedReportModal";
import { getDatePeriod } from "utils/getDatePeriod";
import { Divider } from "components/design/Divider";
import { ExportReconciliationReportsModal } from "./ExportReconciliationReportsModal/ExportReconciliationReportsModal";

type FilterValues = {
  START_DATE: string;
  END_DATE: string;
  SELECT_PERIOD: string;
  ACCOUNTING_METHOD: keyof typeof ACCOUNTING_METHODS;
};

type ExportReportCardProps = {
  title: string;
  description: string;
  onExportClick: () => void;
};

const ExportReportCard = ({
  title,
  description,
  onExportClick,
}: ExportReportCardProps) => {
  return (
    <button
      className="all:unset t-border t-border-solid t-rounded t-border-neutral-10 t-w-full t-p-4 sm:t-px-3 hover:t-bg-surface-lighter-grey t-flex t-justify-start t-h-full"
      onClick={onExportClick}
    >
      <div className="t-flex t-flex-col t-gap-3 t-w-full">
        <div className="t-flex t-justify-between t-text-subtitle t-text-text-100">
          <div>{title}</div>
          <Button
            customType="ghost_icon"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onExportClick();
            }}
          >
            <DownloadIcon />
          </Button>
        </div>
        <Divider />
        <p className="t-text-body-sm !t-mb-0 t-mt-1 t-text-text-30">
          {description}
        </p>
      </div>
    </button>
  );
};

export const AllReportsContent = ({
  entityId,
  groupId,
}: {
  entityId: string;
  groupId: string;
}) => {
  const exportReportsModal = useModal();
  const exportReconModal = useModal();

  const { data: settings } = useGetBookkeepingSettingsQuery(
    {
      entityId,
    },
    { skip: !entityId }
  );
  const { data: startDateData } = useGetStartingDateQuery(
    { groupId, entityId },
    { skip: !groupId || !entityId }
  );
  const bookkeepingStartDate = startDateData?.start_date;

  const defaultDateRange = getDateRangeWithSetting(
    DATE_PERIOD.CURRENT_TAX_YEAR,
    settings?.tax_year_end,
    bookkeepingStartDate
  );

  const { values, updateFilter } = useFilters<FilterValues>({
    initialValue: {
      START_DATE: dayjs(defaultDateRange.startDate).format(YYYY_MM_DD),
      END_DATE: dayjs(defaultDateRange.endDate).format(YYYY_MM_DD),
      SELECT_PERIOD: DATE_PERIOD.CURRENT_TAX_YEAR,
      ACCOUNTING_METHOD: settings?.accounting_method!,
    },
    useQueryParams: true,
  });

  return (
    <DashboardContainer className="t-gap-4 t-h-full">
      <DashboardContainer.Header>
        <div className="t-flex t-flex-col t-gap-3">
          <div className="t-flex t-items-center t-gap-1 t-flex-wrap">
            <span className="t-text-body-sm t-text-text-30">
              Export reports for:
            </span>

            <Filter.Root
              defaultValue="PERIOD"
              capsule={
                <>
                  {Boolean(values?.START_DATE) && Boolean(values?.END_DATE) && (
                    <Chip isRemovable={false} isActive filterType="PERIOD">
                      {formatDate(values.START_DATE)}
                      {" - "}
                      {formatDate(values.END_DATE)}
                    </Chip>
                  )}
                  {Boolean(values?.ACCOUNTING_METHOD) && (
                    <Chip
                      isRemovable={false}
                      isActive
                      filterType="ACCOUNTING_METHOD"
                    >
                      {ACCOUNTING_METHOD_LABELS[values.ACCOUNTING_METHOD]}
                    </Chip>
                  )}
                </>
              }
              trigger={<div className="t-w-0 t-h-8 t-overflow-hidden" />}
              hideDivider
            >
              <Filter.Portal>
                <Filter.List>
                  <Filter.ListItem value="PERIOD">Period</Filter.ListItem>
                  <Filter.ListItem value="ACCOUNTING_METHOD">
                    Accounting method
                  </Filter.ListItem>
                </Filter.List>

                <Filter.Body value="PERIOD" block>
                  <BooksDateFilter
                    updateFilter={updateFilter}
                    values={values}
                    datePeriod={getDatePeriod}
                    showPeriodSelector
                    addMenuPortal
                  />
                </Filter.Body>

                <Filter.Body value="ACCOUNTING_METHOD" block>
                  <>
                    <Label>Accounting method</Label>
                    <Radio.Root
                      onValueChange={(value) =>
                        updateFilter(
                          "ACCOUNTING_METHOD",
                          value as keyof typeof ACCOUNTING_METHODS
                        )
                      }
                      value={values.ACCOUNTING_METHOD}
                    >
                      <Radio.Content className="t-gap-3 t-flex t-flex-col">
                        {ACCOUNTING_METHODS_OPTIONS.map((item) => (
                          <Radio.Item key={item.value} value={item.value}>
                            {item.label}
                          </Radio.Item>
                        ))}
                      </Radio.Content>
                    </Radio.Root>
                  </>
                </Filter.Body>
              </Filter.Portal>
            </Filter.Root>
          </div>
          <AccountingMethodBanner
            selectedAccountingMethod={values.ACCOUNTING_METHOD}
            entityId={entityId}
          />
        </div>
      </DashboardContainer.Header>
      <DashboardContainer.Content>
        <div className="t-flex t-flex-wrap t-gap-5 t-items-stretch">
          <div className="t-w-[calc(50%-10px)] t-flex sm:t-w-full">
            <ExportReportCard
              title="Consolidated Report"
              description="Choose any combination of reports that match your analysis needs -
                Balance Sheet, Income Statement, Aging Summaries, or Revenue
                Recognition - and export them together as a single PDF or Excel
                file."
              onExportClick={exportReportsModal.open}
            />
          </div>
          <div className="t-w-[calc(50%-10px)] t-flex sm:t-w-full">
            <ExportReportCard
              title="Reconciliation Report"
              description="For the selected period, view all completed reconciliations across your bank accounts and download the corresponding reports for any accounts you choose."
              onExportClick={exportReconModal.open}
            />
          </div>
        </div>
      </DashboardContainer.Content>

      <ExportConsolidatedReportModal
        isOpen={exportReportsModal.isOpen}
        onClose={exportReportsModal.close}
        entityId={entityId}
        groupId={groupId}
      />

      <ExportReconciliationReportsModal
        isOpen={exportReconModal.isOpen}
        onClose={exportReconModal.close}
        entityId={entityId}
        groupId={groupId}
      />
    </DashboardContainer>
  );
};
